import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import { UTM_PARAMS } from '../constants';

interface UtmParameterFilterProps {
  searchParam: string;
  searchValue: string;
  onSearchParamChange: (value: string) => void;
  onSearchValueChange: (value: string | null) => void;
}

export default function UtmParameterFilter({
  searchParam,
  searchValue,
  onSearchParamChange,
  onSearchValueChange,
}: UtmParameterFilterProps) {
  const { t } = useTranslation(['reports']);
  const [localValue, setLocalValue] = useState(searchValue);
  const [debouncedValue] = useDebounce(localValue, 500);

  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (debouncedValue === searchValue) return;
    onSearchValueChange(debouncedValue || null);
  }, [debouncedValue, searchValue, onSearchValueChange]);

  return (
    <div className="flex flex-col sm:flex-row items-stretch rounded-md border border-input overflow-hidden focus-within:ring-1 focus-within:ring-ring w-full">
      <div className="w-full sm:w-[180px] flex-shrink-0 sm:border-r border-b sm:border-b-0 border-input [&>div]:w-full [&>div]:gap-0">
        <Select value={searchParam} onValueChange={onSearchParamChange}>
          <SelectTrigger
            className="border-0 rounded-none focus:ring-0 h-10 bg-transparent"
            data-cy="utm-search-param"
          >
            <SelectValue placeholder={t('reports:utmPanel.parameter')} />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {UTM_PARAMS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <input
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={t('reports:utmPanel.search_placeholder')}
        className="flex-1 min-w-0 h-10 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground outline-none"
        data-cy="utm-search-value"
      />
    </div>
  );
}
